import React, { useState } from 'react';
import { FeedCube } from '../services/carouselApiService';
import { Slide } from '../types';
import AuthenticatedImage from './AuthenticatedImage';

interface FeedCubeCardProps {
  cube: FeedCube;
  slides: Slide[];
  shopName?: string;
  onDismiss: (cubeId: string) => void;
  onClick: () => void;
}

const FeedCubeCard: React.FC<FeedCubeCardProps> = ({ cube, slides, shopName, onDismiss, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const sortedImages = slides
    .map(slide => {
      const primary = slide.images?.length > 0
        ? [...slide.images].sort((a, b) => a.displayOrder - b.displayOrder)[0]
        : null;
      return primary ? { image: primary, slide } : null;
    })
    .filter(Boolean) as { image: { id: string; displayOrder: number; filename: string; uploadedAt: string }; slide: Slide }[];

  const hasMultiple = sortedImages.length > 1;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev - 1 + sortedImages.length) % sortedImages.length);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev + 1) % sortedImages.length);
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border-light)',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: hovered ? '0 4px 6px rgba(139, 69, 19, 0.1), 0 2px 4px rgba(139, 69, 19, 0.06)' : '0 1px 3px rgba(139, 69, 19, 0.1)',
        transition: 'all 0.15s ease',
        cursor: 'pointer',
        position: 'relative',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {/* Image area */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', backgroundColor: 'var(--bg-secondary)', overflow: 'hidden' }}>
        {sortedImages.length > 0 ? (
          <>
            <AuthenticatedImage
              shopId={sortedImages[currentImageIndex]?.slide.shopId || ''}
              slideId={sortedImages[currentImageIndex]?.slide.id || ''}
              imageId={sortedImages[currentImageIndex]?.image.id || ''}
              alt={cube.title}
              size="thumb_300"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {/* Title overlay */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              padding: '8px 10px',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
            }}>
              <span style={{
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              }}>
                {cube.title}
              </span>
            </div>
            {/* Carousel arrows */}
            {hasMultiple && (
              <>
                <button
                  onClick={handlePrev}
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: 'var(--text-primary)',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                    opacity: hovered ? 0.9 : 0,
                    transition: 'opacity 0.2s ease',
                    zIndex: 1,
                    padding: 0,
                    lineHeight: 1,
                  }}
                  aria-label="Previous image"
                >&#8249;</button>
                <button
                  onClick={handleNext}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: 'var(--text-primary)',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                    opacity: hovered ? 0.9 : 0,
                    transition: 'opacity 0.2s ease',
                    zIndex: 1,
                    padding: 0,
                    lineHeight: 1,
                  }}
                  aria-label="Next image"
                >&#8250;</button>
              </>
            )}
            {/* Dot indicators */}
            {hasMultiple && (
              <div style={{
                position: 'absolute',
                bottom: '8px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                borderRadius: '12px',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                zIndex: 1,
              }}>
                {sortedImages.map((_, i) => (
                  <div
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i); }}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: i === currentImageIndex ? 'var(--bg-card)' : 'rgba(255, 255, 255, 0.4)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-secondary)' }}>
            <span style={{ fontSize: '2rem' }}>&#9633;</span>
          </div>
        )}
      </div>

      {/* Info bar */}
      <div style={{ padding: '10px 12px' }}>
        {cube.reason && (
          <div style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            lineHeight: 1.4,
            marginBottom: shopName ? '4px' : 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const,
            overflow: 'hidden',
          }}>
            {cube.reason}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {shopName && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{shopName}</span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(cube.cubeId); }}
            title="Dismiss this cube"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--border-medium)',
              padding: '2px 4px',
              fontSize: '0.75rem',
              transition: 'color 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--error-text)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--primary-light)'; }}
          >
            &#10005;
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedCubeCard;
